export type RepeatableRewardKind =
	| "BATTLE_QUESTION_CORRECT"
	| "TOPIC_CARD_COMPLETED"
	| "IC_ATTEMPT_COMPLETED";

/**
 * Diminishing returns policy for repeatable rewards.
 *
 * `repeatIndex` is 0-based: 0 = first time, 1 = second time, ...
 */
export function getRepeatMultiplier(input: {
	kind: RepeatableRewardKind;
	repeatIndex: number;
}): number {
	const repeatIndex = Math.max(0, Math.floor(input.repeatIndex));

	// Topics: full EXP on first pass; diminishing on revise passes (aligned with battle decay).
	if (input.kind === "TOPIC_CARD_COMPLETED") {
		return 0.5 ** repeatIndex;
	}

	// Battles: allow repeats (for spaced repetition) with exponential decay.
	// 1.0, 0.5, 0.25, 0.125, ...
	if (input.kind === "BATTLE_QUESTION_CORRECT") {
		return 0.5 ** repeatIndex;
	}

	// IC scenario success / mission pool: same half-life as topics (`0.5 ** icReviseCycle`).
	if (input.kind === "IC_ATTEMPT_COMPLETED") {
		return 0.5 ** repeatIndex;
	}

	return repeatIndex === 0 ? 1 : 0;
}

/** Matches server topic card EXP scaling via {@link getRepeatMultiplier} for `TOPIC_CARD_COMPLETED`. */
export function applyTopicCardCompletedRepeatMultiplier(
	baseExp: number,
	topicReviseCycle: number,
): number {
	const cycle = Math.max(0, Math.floor(topicReviseCycle));
	const multiplier = getRepeatMultiplier({
		kind: "TOPIC_CARD_COMPLETED",
		repeatIndex: cycle,
	});
	return Math.max(0, Math.floor(baseExp * multiplier));
}

/** IC mission + residual pool scaling via {@link getRepeatMultiplier} for `IC_ATTEMPT_COMPLETED`. */
export function applyIcAttemptCompletedRepeatMultiplier(
	baseExp: number,
	icReviseCycle: number,
): number {
	const cycle = Math.max(0, Math.floor(icReviseCycle));
	const multiplier = getRepeatMultiplier({
		kind: "IC_ATTEMPT_COMPLETED",
		repeatIndex: cycle,
	});
	return Math.max(0, Math.floor(baseExp * multiplier));
}
